import { useEffect } from 'react';

/**
 * Full-screen PDF viewer with an explicit Close button.
 *
 * Previews used to open the PDF in a new tab, which on a phone (and in an
 * installed PWA) left no way back to the editor. This keeps the user in the
 * app: close returns them exactly where they were, with nothing lost.
 *
 * `docs` is [{ label, url }]; with more than one a tab strip switches between
 * them (proposal / agreement).
 */
export default function PdfPreview({ docs, index = 0, onIndex, onClose }) {
  const doc = docs[index] || docs[0];

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="pdf-preview" role="dialog" aria-modal="true" aria-label={`${doc.label} preview`}>
      <div className="pdf-preview-bar">
        <button type="button" className="btn sm pri" onClick={onClose} autoFocus>✕ Close</button>
        {docs.length > 1 ? (
          <div className="pdf-preview-tabs" role="tablist">
            {docs.map((d, i) => (
              <button key={d.label} type="button" role="tab" aria-selected={i === index}
                className={`btn sm ${i === index ? 'on' : ''}`} onClick={() => onIndex?.(i)}>
                {d.label}
              </button>
            ))}
          </div>
        ) : <span className="nm">{doc.label}</span>}
        <a className="btn sm" href={`${doc.url}${doc.url.includes('?') ? '&' : '?'}download=1`}>⬇ Download</a>
      </div>
      {/* key forces a fresh load when switching documents */}
      <iframe key={doc.url} className="pdf-preview-frame" title={`${doc.label} PDF`} src={doc.url} />
    </div>
  );
}
